import React, { useEffect, useState } from 'react';
import { skillsService, BaseEntity, EntityType } from '../../services/skillsService';
import { navigateToTally, NavigationParams } from '../../utils/navigation';

type Node = {
  id: string;
  type: EntityType;
  name: string;
  children?: Node[];
  loaded?: boolean; // children loaded
  parentId?: string;
};

interface SkillsExplorerV2Props {
  urlParams?: NavigationParams;
}

const SkillsExplorerV2: React.FC<SkillsExplorerV2Props> = ({ urlParams }) => {
  const [categories, setCategories] = useState<Node[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [globalLoading, setGlobalLoading] = useState<boolean>(false);
  const [buttonLoadingEdit, setButtonLoadingEdit] = useState<EntityType | null>(null);
  const [buttonLoadingDelete, setButtonLoadingDelete] = useState<EntityType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Node | null>(null);
  const [selectedDetails, setSelectedDetails] = useState<BaseEntity | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [categoryCards, setCategoryCards] = useState<Array<{ id:string; name:string; desc:string; imageUrl?:string; iconUrl?:string }>>([]);
  const [showProgress, setShowProgress] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [editEntityType, setEditEntityType] = useState<'CATEGORY' | 'SKILL' | 'SUBSKILL'>('CATEGORY');
  const [editDetails, setEditDetails] = useState<any>(null);
  const [editImgUrl, setEditImgUrl] = useState<string | null>(null);
  const [editIconUrl, setEditIconUrl] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [deleteInfo, setDeleteInfo] = useState<{ entityType: EntityType; id: string; name: string; parentId?: string; hasChildren: boolean; childrenCount?: number } | null>(null);
  const [categorySkills, setCategorySkills] = useState<Array<{id:string; name:string}>>([]);
  // Skill detail view state
  const [selectedSkill, setSelectedSkill] = useState<{ id:string; name:string } | null>(null);
  const [selectedSkillDetails, setSelectedSkillDetails] = useState<any>(null);
  const [selectedSkillSubskills, setSelectedSkillSubskills] = useState<Array<{id:string; name:string; imageUrl?: string}>>([]);
  const [selectedSkillImgUrl, setSelectedSkillImgUrl] = useState<string | null>(null);
  // Subskill detail view state
  const [selectedSubskill, setSelectedSubskill] = useState<{ id:string; name:string } | null>(null);
  const [selectedSubskillDetails, setSelectedSubskillDetails] = useState<any>(null);
  const [selectedSubskillImgUrl, setSelectedSubskillImgUrl] = useState<string | null>(null);
  // Example-level editing state
  const [exampleEditOpen, setExampleEditOpen] = useState<boolean>(false);
  const [editingExampleIndex, setEditingExampleIndex] = useState<number | null>(null);
  const [editingExample, setEditingExample] = useState<any>(null);
  const [savingExample, setSavingExample] = useState<boolean>(false);
  const [exampleDeleteOpen, setExampleDeleteOpen] = useState<boolean>(false);
  const [exampleDeleteIndex, setExampleDeleteIndex] = useState<number | null>(null);
  // Move functionality state
  const [moveModalOpen, setMoveModalOpen] = useState<boolean>(false);
  const [moveEntityType, setMoveEntityType] = useState<'SKILL' | 'SUBSKILL'>('SKILL');
  const [moveEntityDetails, setMoveEntityDetails] = useState<any>(null);
  const [availableParents, setAvailableParents] = useState<Array<{id: string; name: string}>>([]);
  const [selectedNewParent, setSelectedNewParent] = useState<string>('');
  const [movingEntity, setMovingEntity] = useState<boolean>(false);
  const [isEmbedding, setIsEmbedding] = useState<boolean>(false);

  const generateShortId = (): string => {
    return Math.random().toString(36).slice(2, 8);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setGlobalLoading(true);
      setError(null);
      try {
        const cats = await skillsService.getCategories();
        // Sort categories by display_order
        const sortedCats = [...cats].sort((a:any,b:any)=> (a.display_order ?? 0) - (b.display_order ?? 0));
        setCategories(sortedCats.map((c) => ({ id: c.id, name: c.name, type: 'CATEGORY', children: [], loaded: false })));
        // Build card previews with signed URLs if image/icon keys present
        const cards = await Promise.all(sortedCats.map(async (c) => {
          const card:any = { id: c.id, name: c.name, desc: c.description || '', display_order: (c as any)?.display_order ?? 0 };
          try {
            if ((c as any).image) card.imageUrl = await skillsService.presignView((c as any).image);
          } catch {}
          try {
            if ((c as any).icon) card.iconUrl = await skillsService.presignView((c as any).icon);
          } catch {}
          return card;
        }));
        setCategoryCards(cards);
        
        // Handle URL parameters after categories are loaded
        if (urlParams?.category) {
          const targetCategory = sortedCats.find(c => c.name.toLowerCase() === urlParams.category?.toLowerCase());
          if (targetCategory) {
            // Auto-select and expand the category
            onSelect({ id: targetCategory.id, name: targetCategory.name, type: 'CATEGORY' });
          }
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load categories');
      } finally {
        setLoading(false);
        setGlobalLoading(false);
      }
    };
    load();
    // turn off global after initial load completes
    // handled in finally already via setLoading(false); also setGlobalLoading(false)
  }, []);

  const toggle = async (node: Node) => {
    setExpanded((prev) => ({ ...prev, [node.id]: !prev[node.id] }));
    if (node.type === 'CATEGORY' && !node.loaded) {
      await loadChildren(node, 'SKILL');
    } else if (node.type === 'SKILL' && !node.loaded) {
      await loadChildren(node, 'SUBSKILL');
    }
  };

  const loadChildren = async (node: Node, childType: EntityType) => {
    try {
      setLoading(true);
      if (childType === 'SKILL') {
        const skills = await skillsService.getChildrenByParentId('SKILL', node.id);
        const sorted = [...skills].sort((a:any,b:any)=> (a.display_order ?? 0) - (b.display_order ?? 0));
        updateNodeChildren(node.id, sorted.map((s) => ({ id: s.id, name: s.name, type: 'SKILL', children: [], loaded: false, parentId: node.id })));
      } else if (childType === 'SUBSKILL') {
        const subs = await skillsService.getChildrenByParentId('SUBSKILL', node.id);
        const sorted = [...subs].sort((a:any,b:any)=> (a.display_order ?? 0) - (b.display_order ?? 0));
        updateNodeChildren(node.id, sorted.map((s) => ({ id: s.id, name: s.name, type: 'SUBSKILL', parentId: node.id })));
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load children');
    } finally {
      setLoading(false);
      setGlobalLoading(false);
    }
  };

  const updateNodeChildren = (nodeId: string, children: Node[]) => {
    setCategories((prev) => prev.map((c) => applyUpdate(c, nodeId, children)));
  };

  const onAddSkill = () => {
    try {
      setEditEntityType('SKILL');
      setEditDetails({
        name: '',
        beginner_name: '',
        description: '',
        beginner_description: '',
        display_order: 0,
      });
      setEditImgUrl(null);
      setEditIconUrl(null);
      setEditModalOpen(true);
    } catch (e) {}
  };

  const onAddSubskill = () => {
    try {
      setEditEntityType('SUBSKILL');
      setEditDetails({
        name: '',
        explainer: '',
        explainer_student_normal: '',
        explainer_student_beginner: '',
        qc_status: '',
        display_order: 0,
        examples: { beginner: [], intermediate: [], advanced: [] },
        examples_structured: [],
      });
      setEditImgUrl(null);
      setEditIconUrl(null);
      setEditModalOpen(true);
    } catch (e) {}
  };

  const applyUpdate = (current: Node, nodeId: string, children: Node[]): Node => {
    if (current.id === nodeId) {
      return { ...current, children, loaded: true };
    }
    if (current.children && current.children.length) {
      return { ...current, children: current.children.map((ch) => applyUpdate(ch, nodeId, children)) };
    }
    return current;
  };

  const onSelect = async (node: Node) => {
    try {
      setGlobalLoading(true);
      setSelected(node);
      setSelectedDetails(null);
      const details = await skillsService.getEntityById(node.type, node.id);
      setSelectedDetails(details);
      // Load presigned view URLs for current image/icon if present (category only)
      if (node.type === 'CATEGORY') {
        const imgKey = (details as any)?.image;
        const iconKey = (details as any)?.icon;
        setImgUrl(imgKey ? await skillsService.presignView(imgKey) : null);
        setIconUrl(iconKey ? await skillsService.presignView(iconKey) : null);
        // Load skills for this category
        try {
          const skills = await skillsService.getChildrenByParentId('SKILL', node.id);
          const list: Array<{id:string; name:string; display_order?: number}> = [];
          const sortedSkills = [...(skills as any[])].sort((a:any,b:any)=> (a.display_order ?? 0) - (b.display_order ?? 0));
          for (const s of sortedSkills) {
            const sid = String(s.id);
            list.push({ id: sid, name: String(s.name || ''), display_order: (s as any).display_order });
          }
          setCategorySkills(list);
          
          // Handle skill URL parameter after skills are loaded
          if (urlParams?.skill) {
            const targetSkill = list.find(s => s.name.toLowerCase() === urlParams.skill?.toLowerCase());
            if (targetSkill) {
              // Auto-open the skill
              setTimeout(() => openSkill(targetSkill), 100);
            }
          }
        } catch {}
      } else {
        setImgUrl(null);
        setIconUrl(null);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load details');
    } finally { setGlobalLoading(false); }
  };

  const openSkill = async (skill: {id:string; name:string}) => {
    try {
      console.log('UI: opening skill', skill);
      setGlobalLoading(true);
      setSelectedSkill(skill);
      setSelectedSkillDetails(null);
      setSelectedSkillSubskills([]);
      setSelectedSubskill(null);
      setSelectedSubskillDetails(null);
      setSelectedSubskillImgUrl(null);
      setSelectedSkillImgUrl(null);
      const det = await skillsService.getEntityById('SKILL', skill.id);
      console.log('UI: loaded skill details', det);
      setSelectedSkillDetails(det);
      try {
        const imgKey = (det as any)?.image;
        setSelectedSkillImgUrl(imgKey ? await skillsService.presignView(imgKey) : null);
      } catch {}
      const subs = await skillsService.getChildrenByParentId('SUBSKILL', skill.id);
      console.log('UI: loaded subskills', subs?.length);
      const subCards: Array<{id:string; name:string; imageUrl?:string; display_order?: number}> = [];
      // sort by display_order before rendering
      const sortedSubs = [...subs as any[]].sort((a:any,b:any)=> (a.display_order ?? 0) - (b.display_order ?? 0));
      for (const sub of sortedSubs) {
        const card:any = { id: (sub as any).id, name: (sub as any).name };
        try {
          if ((sub as any).image) card.imageUrl = await skillsService.presignView((sub as any).image);
        } catch {}
        card.display_order = (sub as any).display_order;
        subCards.push(card);
      }
      setSelectedSkillSubskills(subCards);
      // Scroll to top to reveal the skill view
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
    } catch (e:any) { setError(e?.message || 'Failed to load skill'); }
    finally { setGlobalLoading(false); }
  };

  const openSubskill = async (subskill: {id:string; name:string}) => {
    try {
      console.log('UI: opening subskill', subskill);
      setGlobalLoading(true);
      setSelectedSubskill(subskill);
      setSelectedSubskillDetails(null);
      setSelectedSubskillImgUrl(null);
      const det = await skillsService.getEntityById('SUBSKILL', subskill.id);
      setSelectedSubskillDetails(det);
      try {
        const imgKey = (det as any)?.image;
        setSelectedSubskillImgUrl(imgKey ? await skillsService.presignView(imgKey) : null);
      } catch {}
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
    } catch (e:any) { setError(e?.message || 'Failed to load subskill'); }
    finally { setGlobalLoading(false); }
  };

  const fetchChildCount = async (entityType: EntityType, id: string): Promise<number> => {
    const childType = entityType === 'CATEGORY' ? 'SKILL' : entityType === 'SKILL' ? 'SUBSKILL' : null;
    if (!childType) return 0;
    const children = await skillsService.getChildrenByParentId(childType, id);
    return (children || []).length;
  };

  const requestDelete = async (entityType: EntityType, id: string, name: string, parentId?: string) => {
    try {
      setGlobalLoading(true);
      const cnt = await fetchChildCount(entityType, id);
      setDeleteInfo({ entityType, id, name, parentId, hasChildren: cnt > 0, childrenCount: cnt });
      setDeleteModalOpen(true);
    } catch (e:any) {
      setError(e?.message || 'Failed to check child nodes');
    } finally { setGlobalLoading(false); }
  };

  const confirmDelete = async () => {
    if (!deleteInfo) return;
    if (deleteInfo.hasChildren) { setDeleteModalOpen(false); return; }
    try {
      setLoading(true);
      await skillsService.deleteEntity(deleteInfo.entityType, deleteInfo.id);
      // Update UI state
      if (deleteInfo.entityType === 'CATEGORY') {
        const cats = await skillsService.getCategories();
        const sortedCats = [...cats].sort((a:any,b:any)=> (a.display_order ?? 0) - (b.display_order ?? 0));
        setCategories(sortedCats.map((c) => ({ id: c.id, name: c.name, type: 'CATEGORY', children: [], loaded: false })));
        setCategoryCards(await Promise.all(sortedCats.map(async (c:any)=>{
          const card:any = { id:c.id, name:c.name, desc:c.description || '', display_order: c.display_order ?? 0 };
          try { if (c.image) card.imageUrl = await skillsService.presignView(c.image); } catch {}
          try { if (c.icon) card.iconUrl = await skillsService.presignView(c.icon); } catch {}
          return card;
        })));
        setSelected(null);
        setSelectedDetails(null);
      } else if (deleteInfo.entityType === 'SKILL') {
        // Remove from category skills list and return to category view
        setCategorySkills((prev)=> prev.filter(s=> s.id !== deleteInfo.id));
        setSelectedSkill(null);
        setSelectedSkillDetails(null);
        setSelectedSkillSubskills([]);
      } else if (deleteInfo.entityType === 'SUBSKILL') {
        // Remove from subskills list and return to skill view
        setSelectedSkillSubskills((prev)=> prev.filter(ss=> ss.id !== deleteInfo.id));
        setSelectedSubskill(null);
        setSelectedSubskillDetails(null);
      }
    } catch (e:any) {
      setError(e?.message || 'Failed to delete');
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
      setDeleteInfo(null);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      setLoading(true);
      await skillsService.deleteEntity(selected.type, selected.id);
      // Refresh affected branch
      if (selected.type === 'SUBSKILL' && selected.parentId) {
        await loadChildren({ ...selected, id: selected.parentId, type: 'SKILL' }, 'SUBSKILL');
      } else if (selected.type === 'SKILL' && selected.parentId) {
        await loadChildren({ ...selected, id: selected.parentId, type: 'CATEGORY' }, 'SKILL');
      } else if (selected.type === 'CATEGORY') {
        const cats = await skillsService.getCategories();
        setCategories(cats.map((c) => ({ id: c.id, name: c.name, type: 'CATEGORY', children: [], loaded: false })));
      }
      setSelected(null);
      setSelectedDetails(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to delete');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveClick = async (entityType: 'SKILL' | 'SUBSKILL', entity: any) => {
    setMoveEntityType(entityType);
    setMoveEntityDetails(entity);
    setSelectedNewParent(entity.parent_id || '');
    
    // Load available parents based on entity type
    try {
      if (entityType === 'SKILL') {
        const categories = await skillsService.getCategories();
        setAvailableParents(categories.map(c => ({ id: c.id, name: c.name })));
      } else {
        const skills = await skillsService.getAllSkills();
        setAvailableParents(skills.map(s => ({ id: s.id, name: s.name })));
      }
      setMoveModalOpen(true);
    } catch (e: any) {
      setError(`Failed to load available parents: ${e?.message}`);
    }
  };

  const handleMove = async () => {
    if (!moveEntityDetails || !selectedNewParent) return;
    
    try {
      setMovingEntity(true);
      
      // Update the entity with new parent
      const updatedEntity = {
        ...moveEntityDetails,
        parent_id: selectedNewParent,
        parentId: selectedNewParent
      };
      
      await skillsService.updateEntity(moveEntityType, updatedEntity);
      
      // Refresh the UI - reload the affected areas
      if (moveEntityType === 'SKILL') {
        // Reload the category skills list
        setCategorySkills(prev => prev.filter(s => s.id !== moveEntityDetails.id));
        // If we're in skill detail view, go back to category
        if (selectedSkillDetails?.id === moveEntityDetails.id) {
          setSelectedSkill(null);
          setSelectedSkillDetails(null);
          setSelectedSkillSubskills([]);
        }
      } else {
        // Reload the skill subskills list
        setSelectedSkillSubskills(prev => prev.filter(ss => ss.id !== moveEntityDetails.id));
        // If we're in subskill detail view, go back to skill
        if (selectedSubskillDetails?.id === moveEntityDetails.id) {
          setSelectedSubskill(null);
          setSelectedSubskillDetails(null);
        }
      }
      
      setMoveModalOpen(false);
      setError(null);
    } catch (e: any) {
      setError(`Failed to move: ${e?.message}`);
    } finally {
      setMovingEntity(false);
    }
  };

  const renderNode = (node: Node, depth = 0) => {
    const hasChildren = node.type !== 'SUBSKILL';
    const isOpen = !!expanded[node.id];
    return (
      <div key={node.id} style={{ marginLeft: depth * 16 }}>
        <div className="flex items-center py-1">
          {hasChildren && (
            <button className="w-6 text-gray-600" onClick={() => toggle(node)}>{isOpen ? '−' : '+'}</button>
          )}
          <button className={`ml-1 text-sm text-left ${selected?.id===node.id ? 'font-semibold' : ''}`} onClick={() => onSelect(node)}>
            {node.name} <span className="text-gray-400">({node.type})</span>
          </button>
        </div>
        {isOpen && node.children && node.children.map((ch) => renderNode(ch, depth + 1))}
      </div>
    );
  };

  const handleGenerateAllEmbeddings = async () => {
    if (!selectedSubskillDetails) return;

    setIsEmbedding(true);
    setError(null);
    try {
      const result = await skillsService.generateEmbeddingsForSubskill(selectedSubskillDetails.id);
      if (result.success) {
        // Refresh the details view with the updated subskill object that now contains embeddings
        setSelectedSubskillDetails(result.updated_subskill);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate embeddings');
    } finally {
      setIsEmbedding(false);
    }
  };

  return (
    <>
    {globalLoading && (
      <div className="fixed inset-0 z-40 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/10" />
        <div className="relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-talaera-blue" />
        </div>
      </div>
    )}
    <div className="grid grid-cols-3 gap-4">
      {!selected && (
        <div className="col-span-3">
          <div className="max-w-screen-lg mx-auto">
            <div className="border-b border-gray-200 py-6 px-6 bg-white rounded-lg shadow-sm">
              <h1 className="text-2xl font-semibold text-gray-800 text-center">Your Communication Skills</h1>
              <p className="text-gray-600 text-center mt-2">Practicing the lessons and exercises here will help you level up your communication skills in a big way.</p>
              <div className="flex justify-center mt-6 gap-3">
                <button className="bg-[#25394D] hover:brightness-110 text-white py-3 px-6 rounded-md font-medium">
                  Practice Now
                </button>
                <button onClick={()=> setShowProgress((v)=> !v)} className="bg-white border border-gray-300 text-gray-700 py-3 px-6 rounded-md font-medium hover:bg-gray-50">
                  My Progress
                </button>
              </div>
            </div>
            {showProgress && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-2 text-center">Account Exec Requirement</h2>
                  <div className="flex justify-center mb-8 gap-6">
                    <div className="flex items-center gap-2">
                      <svg width="18" height="12">
                        <defs>
                          <linearGradient id="legend-teal-1" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#A5F3FC" />
                            <stop offset="60%" stopColor="#06B6D4" />
                            <stop offset="100%" stopColor="#0E7490" />
                          </linearGradient>
                        </defs>
                        <rect width="18" height="12" rx="3" fill="url(#legend-teal-1)" />
                      </svg>
                      <span className="text-xs text-gray-600">My Levels</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg width="18" height="12">
                        <defs>
                          <linearGradient id="legend-gray-1" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#E5E7EB" />
                            <stop offset="60%" stopColor="#9CA3AF" />
                            <stop offset="100%" stopColor="#1F2937" />
                          </linearGradient>
                        </defs>
                        <rect width="18" height="12" rx="3" fill="url(#legend-gray-1)" />
                      </svg>
                      <span className="text-xs text-gray-600">Target Levels</span>
                    </div>
                  </div>
                  <div className="flex justify-center">
                  {/* Inline SVG radar chart */}
                  {(() => {
                    // Compute the five deterministic scores for the five categories in view order
                    const values = categoryCards.map(c => {
                      const first = String(c.name||'').trim().charAt(0).toUpperCase();
                      const pos = first >= 'A' && first <= 'Z' ? (first.charCodeAt(0)-64) : 1;
                      return ((pos % 10) || 10);
                    }).slice(0,5);
                    const reference = [6,8,8,4,3];
                    const labels = categoryCards.map(c => c.name).slice(0,5);
                    const N = 5;
                    const size = 360; // overall svg size
                    const cx = size/2, cy = size/2;
                    const radius = 150;
                    const toXY = (i:number, val:number) => {
                      const angle = (Math.PI*2*i/N) - Math.PI/2; // start at top
                      const r = (val/10)*radius;
                      return [cx + r*Math.cos(angle), cy + r*Math.sin(angle)];
                    };
                    const points = (values.length===N ? values : new Array(N).fill(0)).map((v,i)=> toXY(i, v));
                    const refPoints = (reference.length===N ? reference : new Array(N).fill(0)).map((v,i)=> toXY(i, v));
                    const gridRings = [2,4,6,8,10];
                    const polygon = points.map(p => p.join(',')).join(' ');
                    const refPolygon = refPoints.map(p => p.join(',')).join(' ');
                    return (
                      <svg width={size} height={size}>
                        {/* grid rings */}
                        {gridRings.map((gr, idx)=> (
                          <polygon key={`g-${idx}`} fill="none" stroke="#E5E7EB" strokeWidth="1"
                            points={new Array(N).fill(0).map((_,i)=> toXY(i, gr).join(',')).join(' ')} />
                        ))}
                        {/* spokes */}
                        {new Array(N).fill(0).map((_,i)=> {
                          const [x,y] = toXY(i, 10);
                          return <line key={`l-${i}`} x1={cx} y1={cy} x2={x} y2={y} stroke="#E5E7EB" strokeWidth="1" />
                        })}
                        {/* animated facets group: translate to center, scale in from 0 */}
                        <g transform={`translate(${cx} ${cy})`}>
                          <g>
                            <animateTransform attributeName="transform" type="scale" from="0" to="1" dur="600ms" fill="freeze" />
                            {/* reference area faceted with 3D-style shading (left lighter, right darker) */}
                            {(() => {
                              const refFacetColors = [
                                '#6B7280', // top - darker gray-500
                                '#374151', // right-top - darker gray-700
                                '#1F2937', // right-bottom - darker gray-800
                                '#9CA3AF', // left-bottom - base gray-400
                                '#6B7280'  // left-top - darker gray-500
                              ];
                              return refPoints.map((rp, i) => {
                                const rpNext = refPoints[(i + 1) % N];
                                const tri = `0,0 ${rp[0]-cx},${rp[1]-cy} ${rpNext[0]-cx},${rpNext[1]-cy}`;
                                return (
                                  <polygon key={`ref-facet-${i}`} points={tri} fill={refFacetColors[i % refFacetColors.length]} fillOpacity="0.3" />
                                );
                              });
                            })()}
                            {/* user data area faceted with 3D-style shading (left lighter, right darker) */}
                            {(() => {
                              const facetColors = [
                                '#06B6D4', // top - base
                                '#0E7490', // right-top - darker
                                '#0891B2', // right-bottom - darker
                                '#67E8F9', // left-bottom - lighter
                                '#A5F3FC'  // left-top - lighter
                              ];
                              return points.map((p, i) => {
                                const pNext = points[(i + 1) % N];
                                const tri = `0,0 ${p[0]-cx},${p[1]-cy} ${pNext[0]-cx},${pNext[1]-cy}`;
                                return (
                                  <polygon key={`facet-${i}`} points={tri} fill={facetColors[i % facetColors.length]} fillOpacity="0.45" />
                                );
                              });
                            })()}
                          </g>
                        </g>
                        {/* labels */}
                        {(() => {
                          const labelRadius = radius * 1.12;
                          const toXYLabel = (i:number) => {
                            const angle = (Math.PI*2*i/N) - Math.PI/2;
                            return [cx + labelRadius*Math.cos(angle), cy + labelRadius*Math.sin(angle)];
                          };
                          return labels.map((lbl, i)=> {
                            const [x,y] = toXYLabel(i);
                            const lower = String(lbl||'').toLowerCase();
                            const isCulture = lower.includes('culture') && lower.includes('context');
                            const isSharingIdeas = lower.includes('sharing') && lower.includes('ideas');
                            if (isCulture) {
                              return (
                                <text key={`t-${i}`} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="10" fill="#374151">
                                  <tspan x={x} dy="-0.35em">Culture &amp;</tspan>
                                  <tspan x={x} dy="1.2em">Context</tspan>
                                </text>
                              );
                            }
                            if (isSharingIdeas) {
                              return (
                                <text key={`t-${i}`} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="10" fill="#374151">
                                  <tspan x={x} dy="-0.35em">Sharing</tspan>
                                  <tspan x={x} dy="1.2em">Ideas</tspan>
                                </text>
                              );
                            }
                            return <text key={`t-${i}`} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="10" fill="#374151">{lbl}</text>
                          });
                        })()}
                      </svg>
                    );
                  })()}
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-2 text-center">Company Average</h2>
                  <div className="flex justify-center mb-8 gap-6">
                    <div className="flex items-center gap-2">
                      <svg width="18" height="12">
                        <defs>
                          <linearGradient id="legend-teal-2" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#A5F3FC" />
                            <stop offset="60%" stopColor="#06B6D4" />
                            <stop offset="100%" stopColor="#0E7490" />
                          </linearGradient>
                        </defs>
                        <rect width="18" height="12" rx="3" fill="url(#legend-teal-2)" />
                      </svg>
                      <span className="text-xs text-gray-600">My Levels</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg width="18" height="12">
                        <defs>
                          <linearGradient id="legend-gray-2" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#E5E7EB" />
                            <stop offset="60%" stopColor="#9CA3AF" />
                            <stop offset="100%" stopColor="#1F2937" />
                          </linearGradient>
                        </defs>
                        <rect width="18" height="12" rx="3" fill="url(#legend-gray-2)" />
                      </svg>
                      <span className="text-xs text-gray-600">Company Average</span>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    {(() => {
                      const values = categoryCards.map(c => {
                        const first = String(c.name||'').trim().charAt(0).toUpperCase();
                        const pos = first >= 'A' && first <= 'Z' ? (first.charCodeAt(0)-64) : 1;
                        return ((pos % 10) || 10);
                      }).slice(0,5);
                      const reference = [6,7,6,3,3];
                      const labels = categoryCards.map(c => c.name).slice(0,5);
                      // Set specific spokes by name where present
                      const lc = (s:string)=> String(s||'').toLowerCase();
                      const idxCulture = labels.findIndex(l => lc(l).includes('culture') && lc(l).includes('context'));
                      if (idxCulture >= 0) reference[idxCulture] = 5; // Culture & Context -> 5
                      const idxConversation = labels.findIndex(l => lc(l).includes('conversation'));
                      if (idxConversation >= 0) reference[idxConversation] = 6; // Conversation -> 6
                      const N = 5;
                      const size = 360; // overall svg size
                      const cx = size/2, cy = size/2;
                      const radius = 150;
                      const toXY = (i:number, val:number) => {
                        const angle = (Math.PI*2*i/N) - Math.PI/2; // start at top
                        const r = (val/10)*radius;
                        return [cx + r*Math.cos(angle), cy + r*Math.sin(angle)];
                      };
                      const points = (values.length===N ? values : new Array(N).fill(0)).map((v,i)=> toXY(i, v));
                      const refPoints = (reference.length===N ? reference : new Array(N).fill(0)).map((v,i)=> toXY(i, v));
                      const gridRings = [2,4,6,8,10];
                      const polygon = points.map(p => p.join(',')).join(' ');
                      const refPolygon = refPoints.map(p => p.join(',')).join(' ');
                      const labelRadius = radius * 1.08;
                      const toXYLabel = (i:number) => {
                        const angle = (Math.PI*2*i/N) - Math.PI/2;
                        return [cx + labelRadius*Math.cos(angle), cy + labelRadius*Math.sin(angle)];
                      };
                      return (
                        <svg width={size} height={size}>
                          {gridRings.map((gr, idx)=> (
                            <polygon key={`g2-${idx}`} fill="none" stroke="#E5E7EB" strokeWidth="1"
                              points={new Array(N).fill(0).map((_,i)=> toXY(i, gr).join(',')).join(' ')} />
                          ))}
                          {new Array(N).fill(0).map((_,i)=> {
                            const [x,y] = toXY(i, 10);
                            return <line key={`l2-${i}`} x1={cx} y1={cy} x2={x} y2={y} stroke="#E5E7EB" strokeWidth="1" />
                          })}
                          {/* animated facets group: translate to center, scale in from 0 */}
                          <g transform={`translate(${cx} ${cy})`}>
                            <g>
                              <animateTransform attributeName="transform" type="scale" from="0" to="1" dur="600ms" fill="freeze" />
                              {/* reference area faceted with 3D-style shading (left lighter, right darker) */}
                              {(() => {
                                const refFacetColors = [
                                  '#6B7280',
                                  '#374151',
                                  '#1F2937',
                                  '#9CA3AF',
                                  '#6B7280'
                                ];
                                return refPoints.map((rp, i) => {
                                  const rpNext = refPoints[(i + 1) % N];
                                  const tri = `0,0 ${rp[0]-cx},${rp[1]-cy} ${rpNext[0]-cx},${rpNext[1]-cy}`;
                                  return (
                                    <polygon key={`ref-facet2-${i}`} points={tri} fill={refFacetColors[i % refFacetColors.length]} fillOpacity="0.3" />
                                  );
                                });
                              })()}
                              {/* user data area faceted with 3D-style shading (left lighter, right darker) */}
                              {(() => {
                                const facetColors = [
                                  '#06B6D4',
                                  '#0E7490',
                                  '#0891B2',
                                  '#67E8F9',
                                  '#A5F3FC'
                                ];
                                return points.map((p, i) => {
                                  const pNext = points[(i + 1) % N];
                                  const tri = `0,0 ${p[0]-cx},${p[1]-cy} ${pNext[0]-cx},${pNext[1]-cy}`;
                                  return (
                                    <polygon key={`facet2-${i}`} points={tri} fill={facetColors[i % facetColors.length]} fillOpacity="0.45" />
                                  );
                                });
                              })()}
                            </g>
                          </g>
                          {labels.map((lbl, i)=> {
                            const [x,y] = toXYLabel(i);
                            const lower = String(lbl||'').toLowerCase();
                            const isCulture = lower.includes('culture') && lower.includes('context');
                            const isSharingIdeas = lower.includes('sharing') && lower.includes('ideas');
                            if (isCulture) {
                              return (
                                <text key={`t2-${i}`} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="10" fill="#374151">
                                  <tspan x={x} dy="-0.35em">Culture &amp;</tspan>
                                  <tspan x={x} dy="1.2em">Context</tspan>
                                </text>
                              );
                            }
                            if (isSharingIdeas) {
                              return (
                                <text key={`t2-${i}`} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="10" fill="#374151">
                                  <tspan x={x} dy="-0.35em">Sharing</tspan>
                                  <tspan x={x} dy="1.2em">Ideas</tspan>
                                </text>
                              );
                            }
                            return <text key={`t2-${i}`} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="10" fill="#374151">{lbl}</text>
                          })}
                        </svg>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
            <div className="mt-4">
              {categoryCards.map((card) => (
                <div key={card.id} className="relative">
                  <button onClick={() => onSelect({ id: card.id, name: card.name, type: 'CATEGORY' })} className="w-full text-left">
                    <div className="bg-white border border-gray-200 rounded-lg mb-4 overflow-hidden flex shadow-sm hover:shadow transition">
                      <div className="w-[180px] h-[112px] flex-shrink-0 overflow-hidden bg-gray-100">
                        {card.imageUrl ? (
                          <img src={card.imageUrl} alt={card.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">No image</div>
                        )}
                      </div>
                      <div className="flex-1 p-4 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-start">
                            <div className="text-blue-400 mr-2 mt-1">
                              {card.iconUrl ? <img src={card.iconUrl} alt="icon" className="w-5 h-5 object-cover rounded" /> : <div className="w-5 h-5 rounded bg-blue-200" />}
                            </div>
                            <h2 className="text-lg font-semibold text-gray-700">{card.name}</h2>
                          </div>
                          <p className="text-gray-600 mt-1 text-sm">{card.desc}</p>
                          {/* Thin progress bar for category card */}
                          {(() => {
                            const name = String(card.name || '');
                            const first = name.trim().charAt(0).toUpperCase();
                            const alphaPos = first >= 'A' && first <= 'Z' ? (first.charCodeAt(0) - 64) : 1;
                            const score = ((alphaPos % 10) || 10); // 1-10
                            const pct = Math.max(0, Math.min(100, (score / 10) * 100));
                            const colorClass = score <= 3 ? 'bg-red-500' : (score <= 7 ? 'bg-yellow-400' : 'bg-green-500');
                            return (
                              <div className="mt-3">
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div className={`h-2 ${colorClass}`} style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="flex items-center pr-4">
                        <span className="text-gray-400">›</span>
                      </div>
                    </div>
                  </button>
                  <button
                    title="Edit"
                    onClick={async (e)=>{
                      e.stopPropagation();
                      try {
                        setLoading(true);
                        const det = await skillsService.getEntityById('CATEGORY', card.id);
                        setEditDetails(det);
                        setEditImgUrl((det as any)?.image ? await skillsService.presignView((det as any).image) : null);
                        setEditIconUrl((det as any)?.icon ? await skillsService.presignView((det as any).icon) : null);
                        setEditModalOpen(true);
                      } catch (err:any) { setError(err?.message || 'Failed to open editor'); } finally { setLoading(false); }
                    }}
                    className="absolute top-2 right-3 p-2 bg-white/90 hover:bg-white rounded-full shadow border"
                  >
                    <img src="/pencil-edit.svg" alt="Edit" className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Subskill detail page (simple for now) */}
      {selected && selected.type === 'CATEGORY' && selectedSkill && selectedSubskill && (
        <div className="col-span-3">
          <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-sm border">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <button className="text-gray-600 mr-4" onClick={()=> setSelectedSubskill(null)} title="Back to skill">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                  </button>
                  <h1 className="text-3xl font-semibold text-gray-700">{selectedSubskill.name}</h1>
                </div>
                {/* QC status pill between title and actions */}
                <div className="flex items-center">
                  {((selectedSubskillDetails as any)?.qc_status) && (
                    ((String((selectedSubskillDetails as any)?.qc_status).toLowerCase()==='approved') ? (
                      <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium">QC Approved</span>
                    ) : (
                      <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">QC: {(selectedSubskillDetails as any)?.qc_status}</span>
                    ))
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={handleGenerateAllEmbeddings}
                    disabled={isEmbedding}
                    className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isEmbedding ? 'Generating...' : 'Generate Embeddings'}
                  </button>
                  <button
                    title="Edit"
                    onClick={async ()=>{
                      try {
                        setLoading(true);
                        setButtonLoadingEdit('SUBSKILL');
                        const det = await skillsService.getEntityById('SUBSKILL', (selectedSubskillDetails as any)?.id || selectedSubskill.id);
                        setEditEntityType('SUBSKILL');
                        setEditDetails(det);
                        setEditImgUrl((det as any)?.image ? await skillsService.presignView((det as any).image) : null);
                        setEditIconUrl(null);
                        setEditModalOpen(true);
                      } catch (err:any) { setError(err?.message || 'Failed to open editor'); } finally { setLoading(false); setButtonLoadingEdit(null); }
                    }}
                    className="p-2 bg-white/90 hover:bg-white rounded-full shadow border"
                  >
                    {buttonLoadingEdit==='SUBSKILL' ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-talaera-blue" />
                    ) : (
                      <img src="/pencil-edit.svg" alt="Edit" className="h-5 w-5" />
                    )}
                  </button>
                  <button
                    title="Move"
                    onClick={async ()=>{
                      if (selectedSubskillDetails) {
                        await handleMoveClick('SUBSKILL', selectedSubskillDetails);
                      }
                    }}
                    className="p-2 bg-white/90 hover:bg-white rounded-full shadow border"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                  </button>
                  <button title="Delete" onClick={async ()=>{ try { setButtonLoadingDelete('SUBSKILL'); await requestDelete('SUBSKILL', (selectedSubskillDetails as any)?.id || selectedSubskill.id, selectedSubskill.name, (selectedSkill as any)?.id); } finally { setButtonLoadingDelete(null); } }} className="p-2 bg-white/90 hover:bg-white rounded-full shadow border">
                    {buttonLoadingDelete==='SUBSKILL' ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-talaera-blue" />
                    ) : (
                      <img src="/delete.svg" alt="Delete" className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Tags and Level under title */}
              <div className="mb-4">
                <div className="flex flex-wrap items-center gap-2">
                  {Array.isArray(((selectedSubskillDetails as any)?.category_tags)) && ((selectedSubskillDetails as any)?.category_tags?.length > 0) && (
                    <>
                      <span className="text-sm font-medium text-gray-700 mr-1">Category Tags:</span>
                      {(((selectedSubskillDetails as any)?.category_tags) || []).map((tag: string, idx: number)=> (
                        <span key={`head-tag-${idx}`} className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">{tag}</span>
                      ))}
                    </>
                  )}
                  {Array.isArray(((selectedSubskillDetails as any)?.cefr_levels)) && ((selectedSubskillDetails as any)?.cefr_levels?.length > 0) && (
                    <>
                      <span className="ml-3 text-sm font-medium text-gray-700">CEFR Levels:</span>
                      {(((selectedSubskillDetails as any)?.cefr_levels) || []).map((lvl: string, idx: number)=> (
                        <span key={`head-cefr-${idx}`} className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">{lvl}</span>
                      ))}
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-col md:flex-row justify-between">
                <div className="md:w-1/2 pr-4">
                  <p className="text-xl text-gray-700 mb-8">{selectedSubskillDetails?.description || ' '}</p>
                  <div className="flex space-x-4">
                    <button 
                      onClick={() => {
                        const context = {
                          categoryDescription: (selectedDetails as any)?.description,
                          skillDescription: (selectedSkillDetails as any)?.description,
                          subskillDescription: (selectedSubskillDetails as any)?.description,
                          subskillExplainer: (selectedSubskillDetails as any)?.explainer,
                          examples: (selectedSubskillDetails as any)?.examples || []
                        };
                        
                        navigateToTally({
                          topic: `Practice ${selectedSubskill.name}`,
                          category: (selectedDetails as any)?.name,
                          skill: selectedSkill.name,
                          scenario: selectedSubskill.name,
                          context: JSON.stringify(context),
                          autoStart: true
                        });
                      }}
                      className="border-2 border-gray-700 text-gray-700 px-6 py-3 rounded-md font-medium hover:bg-gray-100 transition-colors"
                    >
                      Talk to Tally
                    </button>
                    <button className="bg-[#25394D] text-white px-10 py-3 rounded-md font-medium hover:brightness-110 transition-all">Practice Now</button>
                  </div>
                </div>
                <div className="md:w-1/2 mt-6 md:mt-0 mb-8">
                  {selectedSubskillImgUrl ? (
                    <img src={selectedSubskillImgUrl} alt={selectedSubskill.name} className="rounded-lg w-full h-auto" />
                  ) : (
                    <div className="w-full h-64 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">No Image Available</div>
                  )}
                </div>
              </div>
            </div>

            {/* Structured sections */}
            <div className="border-t border-blue-200 p-0 pb-6 bg-[#F5F1EF]">
                <div className="space-y-6 p-6">
                  {/* Explainers card */}
                  <div className="border border-gray-200 rounded-lg p-6 bg-white mx-6">
                    <div className="space-y-3">
                      <div className="mb-2"><span className="font-medium">Explainer:</span> {(selectedSubskillDetails as any)?.explainer || ''}</div>
                      <div className="mb-2"><span className="font-medium">Explainer (Student Normal):</span> {(selectedSubskillDetails as any)?.explainer_student_normal || ''}</div>
                      <div className="mb-2"><span className="font-medium">Explainer (Student Beginner):</span> {(selectedSubskillDetails as any)?.explainer_student_beginner || ''}</div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Structured Examples</h3>
                    {((selectedSubskillDetails as any)?.examples_structured || []).map((ex: any, idx: number) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-md border border-gray-200 mb-2">
                        <p className="text-sm text-gray-800 font-medium">"{ex.text}"</p>
                        <p className="text-xs text-gray-500 mt-1"><strong>Context:</strong> {ex.context}</p>
                        <p className="text-xs text-gray-500"><strong>Level:</strong> {ex.level}</p>
                      </div>
                    ))}
                    {((selectedSubskillDetails as any)?.examples_structured || []).length === 0 && (
                      <p className="text-gray-500 text-sm">No structured examples provided.</p>
                    )}
                  </div>

                  {/* New Embeddings Section */}
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Embeddings</h3>
                    {((selectedSubskillDetails as any)?.examples_structured || []).some((ex: any) => ex.embedding) ? (
                      ((selectedSubskillDetails as any)?.examples_structured || []).filter((ex: any) => ex.embedding).map((ex: any, idx: number) => (
                        <div key={`embed-${idx}`} className="p-3 bg-gray-50 rounded-md border border-gray-200 mb-2">
                          <p className="text-sm text-gray-800 font-medium">"{ex.text}"</p>
                          <div className="mt-2 p-2 bg-gray-100 rounded">
                            <p className="text-xs font-mono break-all text-gray-600">
                              Vector (first 5 dims): [{ex.embedding?.slice(0, 5).join(', ')}...]
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">No embeddings generated yet. Click the "Generate Embeddings" button to create them.</p>
                    )}
                  </div>

                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Usage Notes</h3>
                    <p className="text-gray-600 mb-2"><strong>Beginner-friendly Description:</strong> {selectedSubskillDetails?.beginner_description || 'N/A'}</p>
                  </div>
                </div>

                {/* Structured examples as horizontal sections (always show all sections) */}
                <div className="mt-6 px-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Examples</h4>

                  {/* Beginner section */}
                  <div className="mt-4 border border-gray-200 rounded-lg p-6 bg-white mx-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-gray-700 font-medium">Beginner</div>
                      <button title="Add Example" className="p-2 bg-white/90 hover:bg-white rounded-full shadow border" onClick={() => {
                        setEditingExampleIndex(null);
                        setEditingExample({ level: 'beginner', text: '', context: '', example_id: generateShortId() });
                        setExampleEditOpen(true);
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5 text-green-600"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v14m7-7H5"/></svg>
                      </button>
                    </div>
                    {(((selectedSubskillDetails as any)?.examples_structured) || [])
                      .filter((ex: any)=> String(ex.level||'').toLowerCase()==='beginner')
                      .map((ex: any, idx: number)=> (
                        <div key={`sbx-${ex.example_id || idx}`} className="relative p-3 bg-gray-50 rounded border border-blue-100 shadow-sm mb-2">
                          <div className="absolute top-2 right-2 flex items-center gap-1">
                            <button title="Edit example" className="p-1.5 bg-white/90 hover:bg-white rounded-full shadow border" onClick={()=>{
                              const all = (((selectedSubskillDetails as any)?.examples_structured) || []);
                              const globalIdx = all.indexOf(ex) !== -1 ? all.indexOf(ex) : all.findIndex((e:any)=> e.example_id && e.example_id === ex.example_id);
                              setEditingExampleIndex(globalIdx >= 0 ? globalIdx : null);
                              setEditingExample({ level: ex.level || '', text: ex.text || '', context: ex.context || '' });
                              setExampleEditOpen(true);
                            }}>
                              <img src="/pencil-edit.svg" alt="Edit" className="h-4 w-4" />
                            </button>
                            <button title="Delete example" className="p-1.5 bg-white/90 hover:bg-white rounded-full shadow border" onClick={()=>{
                              const all = (((selectedSubskillDetails as any)?.examples_structured) || []);
                              const idx = all.indexOf(ex) !== -1 ? all.indexOf(ex) : all.findIndex((e:any)=> e.example_id && e.example_id === ex.example_id);
                              setExampleDeleteIndex(idx >= 0 ? idx : null);
                              setExampleDeleteOpen(true);
                            }}>
                              <img src="/delete.svg" alt="Delete" className="h-4 w-4" />
                            </button>
                          </div>
                          {ex.context && <div className="text-sm text-gray-600 mb-1">{ex.context}</div>}
                          <div className="mb-1">{ex.text}</div>
                        </div>
                      ))}
                  </div>

                  {/* Intermediate section */}
                  <div className="mt-4 border border-gray-200 rounded-lg p-6 bg-white mx-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-gray-700 font-medium">Intermediate</div>
                      <button title="Add Example" className="p-2 bg-white/90 hover:bg-white rounded-full shadow border" onClick={() => {
                        setEditingExampleIndex(null);
                        setEditingExample({ level: 'intermediate', text: '', context: '', example_id: generateShortId() });
                        setExampleEditOpen(true);
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5 text-green-600"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v14m7-7H5"/></svg>
                      </button>
                    </div>
                    {(((selectedSubskillDetails as any)?.examples_structured) || [])
                      .filter((ex: any)=> String(ex.level||'').toLowerCase()==='intermediate')
                      .map((ex: any, idx: number)=> (
                        <div key={`six-${ex.example_id || idx}`} className="relative p-3 bg-gray-50 rounded border border-blue-100 shadow-sm mb-2">
                          <div className="absolute top-2 right-2 flex items-center gap-1">
                            <button title="Edit example" className="p-1.5 bg-white/90 hover:bg-white rounded-full shadow border" onClick={()=>{
                              const all = (((selectedSubskillDetails as any)?.examples_structured) || []);
                              const globalIdx = all.indexOf(ex) !== -1 ? all.indexOf(ex) : all.findIndex((e:any)=> e.example_id && e.example_id === ex.example_id);
                              setEditingExampleIndex(globalIdx >= 0 ? globalIdx : null);
                              setEditingExample({ level: ex.level || '', text: ex.text || '', context: ex.context || '' });
                              setExampleEditOpen(true);
                            }}>
                              <img src="/pencil-edit.svg" alt="Edit" className="h-4 w-4" />
                            </button>
                            <button title="Delete example" className="p-1.5 bg-white/90 hover:bg-white rounded-full shadow border" onClick={()=>{
                              const all = (((selectedSubskillDetails as any)?.examples_structured) || []);
                              const idx = all.indexOf(ex) !== -1 ? all.indexOf(ex) : all.findIndex((e:any)=> e.example_id && e.example_id === ex.example_id);
                              setExampleDeleteIndex(idx >= 0 ? idx : null);
                              setExampleDeleteOpen(true);
                            }}>
                              <img src="/delete.svg" alt="Delete" className="h-4 w-4" />
                            </button>
                          </div>
                          {ex.context && <div className="text-sm text-gray-600 mb-1">{ex.context}</div>}
                          <div className="mb-1">{ex.text}</div>
                        </div>
                      ))}
                  </div>

                  {/* Advanced section */}
                  <div className="mt-4 border border-gray-200 rounded-lg p-6 bg-white mx-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-gray-700 font-medium">Advanced</div>
                      <button title="Add Example" className="p-2 bg-white/90 hover:bg-white rounded-full shadow border" onClick={() => {
                        setEditingExampleIndex(null);
                        setEditingExample({ level: 'advanced', text: '', context: '', example_id: generateShortId() });
                        setExampleEditOpen(true);
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5 text-green-600"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v14m7-7H5"/></svg>
                      </button>
                    </div>
                    {(((selectedSubskillDetails as any)?.examples_structured) || [])
                      .filter((ex: any)=> String(ex.level||'').toLowerCase()==='advanced')
                      .map((ex: any, idx: number)=> (
                        <div key={`sax-${ex.example_id || idx}`} className="relative p-3 bg-gray-50 rounded border border-blue-100 shadow-sm mb-2">
                          <div className="absolute top-2 right-2 flex items-center gap-1">
                            <button title="Edit example" className="p-1.5 bg-white/90 hover:bg-white rounded-full shadow border" onClick={()=>{
                              const all = (((selectedSubskillDetails as any)?.examples_structured) || []);
                              const globalIdx = all.indexOf(ex) !== -1 ? all.indexOf(ex) : all.findIndex((e:any)=> e.example_id && e.example_id === ex.example_id);
                              setEditingExampleIndex(globalIdx >= 0 ? globalIdx : null);
                              setEditingExample({ level: ex.level || '', text: ex.text || '', context: ex.context || '' });
                              setExampleEditOpen(true);
                            }}>
                              <img src="/pencil-edit.svg" alt="Edit" className="h-4 w-4" />
                            </button>
                            <button title="Delete example" className="p-1.5 bg-white/90 hover:bg-white rounded-full shadow border" onClick={()=>{
                              const all = (((selectedSubskillDetails as any)?.examples_structured) || []);
                              const idx = all.indexOf(ex) !== -1 ? all.indexOf(ex) : all.findIndex((e:any)=> e.example_id && e.example_id === ex.example_id);
                              setExampleDeleteIndex(idx >= 0 ? idx : null);
                              setExampleDeleteOpen(true);
                            }}>
                              <img src="/delete.svg" alt="Delete" className="h-4 w-4" />
                            </button>
                          </div>
                          {ex.context && <div className="text-sm text-gray-600 mb-1">{ex.context}</div>}
                          <div className="mb-1">{ex.text}</div>
                        </div>
                      ))}
                  </div>
                </div>

                
              </div>
            </div>
          </div>
      )}

      {selected && selected.type !== 'CATEGORY' && (
        <>
          <div className="col-span-1 p-4 bg-white rounded-lg shadow border">
            <h2 className="text-lg font-semibold mb-2">Skills Explorer (V2)</h2>
            {error && <div className="p-2 bg-red-50 text-red-700 rounded mb-2">{error}</div>}
            {loading && <div className="text-gray-500 mb-2">Loading...</div>}
            <div>
              {categories.map((c) => renderNode(c))}
            </div>
          </div>
          <div className="col-span-2 p-4 bg-white rounded-lg shadow border">
            <h3 className="text-md font-semibold mb-2">Details</h3>
            {!selected && <div className="text-gray-500">Select a node</div>}
            {selected && (
              <div>
            <div className="mb-2 text-sm text-gray-600">{selected.type}</div>
            <div className="mb-2"><span className="font-medium">Name:</span> {selectedDetails?.name || selected.name}</div>
            {selectedDetails && (
              <>
                <div className="mb-2"><span className="font-medium">Beginner Name:</span> {selectedDetails.beginner_name}</div>
                <div className="mb-2"><span className="font-medium">Description:</span> {selectedDetails.description}</div>
                <div className="mb-4"><span className="font-medium">Beginner Description:</span> {selectedDetails.beginner_description}</div>

                {/* SubSkill extra fields */}
                {selected.type === 'SUBSKILL' && (
                  <>
                    <div className="mb-2"><span className="font-medium">Explainer:</span> {(selectedDetails as any)?.explainer || ''}</div>
                    <div className="mb-2"><span className="font-medium">Explainer (Student Normal):</span> {(selectedDetails as any)?.explainer_student_normal || ''}</div>
                    <div className="mb-4"><span className="font-medium">Explainer (Student Beginner):</span> {(selectedDetails as any)?.explainer_student_beginner || ''}</div>

                    <div className="mb-2"><span className="font-medium">Category Tags:</span> {((selectedDetails as any)?.category_tags || []).join(', ')}</div>
                    <div className="mb-4"><span className="font-medium">CEFR Levels:</span> {((selectedDetails as any)?.cefr_levels || []).join(', ')}</div>

                    <div className="mb-2"><span className="font-medium">QC Status:</span> {(selectedDetails as any)?.qc_status || ''}</div>
                    

                    {/* Aggregated examples */}
                    <div className="mb-4">
                      <h4 className="font-semibold">Examples</h4>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Beginner</div>
                          {selectedDetails.examples?.beginner?.map((ex, idx) => (
                            <div key={`b-${idx}`} className="p-2 bg-gray-50 rounded mb-1">{ex}</div>
                          ))}
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Intermediate</div>
                          {selectedDetails.examples?.intermediate?.map((ex, idx) => (
                            <div key={`i-${idx}`} className="p-2 bg-gray-50 rounded mb-1">{ex}</div>
                          ))}
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Advanced</div>
                          {selectedDetails.examples?.advanced?.map((ex, idx) => (
                            <div key={`a-${idx}`} className="p-2 bg-gray-50 rounded mb-1">{ex}</div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Structured examples */}
                    <div className="mb-4">
                      <h4 className="font-semibold">Structured Examples</h4>
                      <div className="space-y-2 mt-2">
                        {(((selectedDetails as any)?.examples_structured) || []).map((ex: any, idx: number) => (
                          <div key={`sx-${idx}`} className="p-2 bg-gray-50 rounded">
                            <div className="text-xs text-gray-600 mb-1">{ex.level} · {ex.example_id}</div>
                            <div className="mb-1">{ex.text}</div>
                            {ex.context && <div className="text-sm text-gray-600">Context: {ex.context}</div>}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Other arrays */}
                    <div className="mb-2"><span className="font-medium">Counter Examples:</span> {(((selectedDetails as any)?.counter_examples) || []).length}</div>
                    <div className="mb-2"><span className="font-medium">Practice Prompts:</span> {(((selectedDetails as any)?.practice_prompts) || []).length}</div>
                    <div className="mb-2"><span className="font-medium">Assessment Criteria:</span> {(((selectedDetails as any)?.assessment_criteria) || []).length}</div>
                    <div className="mb-4"><span className="font-medium">Collocations:</span> {(((selectedDetails as any)?.collocations) || []).length}</div>
                  </>
                )}

                {/* Skill extra arrays */}
                {selected.type === 'SKILL' && (
                  <>
                    <div className="mb-2"><span className="font-medium">Practice Contexts:</span> {(((selectedDetails as any)?.practice_contexts) || []).length}</div>
                    <div className="mb-2"><span className="font-medium">Common Challenges:</span> {(((selectedDetails as any)?.common_challenges) || []).length}</div>
                    <div className="mb-2"><span className="font-medium">Teaching Points:</span> {(((selectedDetails as any)?.teaching_points) || []).length}</div>
                    <div className="mb-4"><span className="font-medium">Related Skills:</span> {(((selectedDetails as any)?.related_skills) || []).length}</div>
                  </>
                )}

                {/* Category extras removed in non-category view */}
              </>
            )}
            <div className="flex gap-2">
              <button onClick={handleDelete} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
            </div>
              </div>
            )}
          </div>
        </>
      )}

      {selected && selected.type === 'CATEGORY' && !selectedSkill && (
        <div className="col-span-3">
          <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-sm border">
            {/* Header section */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                <button className="text-gray-600 mr-4" onClick={()=>{ setSelected(null); setIsEditing(false); }} title="Back">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                </button>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    {iconUrl ? <img src={iconUrl} alt="icon" className="w-6 h-6 object-cover rounded" /> : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                    )}
                  </div>
                  <h1 className="text-3xl font-semibold text-gray-700">{selectedDetails?.name}</h1>
                </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    title="Edit"
                    onClick={async ()=>{
                      try {
                        setLoading(true);
                        setButtonLoadingEdit('CATEGORY');
                        const det = await skillsService.getEntityById('CATEGORY', (selectedDetails as any).id);
                        setEditEntityType('CATEGORY');
                        setEditDetails(det);
                        setEditImgUrl((det as any)?.image ? await skillsService.presignView((det as any).image) : null);
                        setEditIconUrl((det as any)?.icon ? await skillsService.presignView((det as any).icon) : null);
                        setEditModalOpen(true);
                      } catch (err:any) { setError(err?.message || 'Failed to open editor'); } finally { setLoading(false); setButtonLoadingEdit(null); }
                    }}
                    className="p-2 bg-white/90 hover:bg-white rounded-full shadow border"
                  >
                    {buttonLoadingEdit==='CATEGORY' ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-talaera-blue" />
                    ) : (
                      <img src="/pencil-edit.svg" alt="Edit" className="h-5 w-5" />
                    )}
                  </button>
                  <button
                    title="Delete"
                    onClick={async ()=>{
                      try {
                        setButtonLoadingDelete('CATEGORY');
                        await requestDelete('CATEGORY', (selectedDetails as any)?.id, (selectedDetails as any)?.name);
                      } finally { setButtonLoadingDelete(null); }
                    }}
                    className="p-2 bg-white/90 hover:bg-white rounded-full shadow border"
                  >
                    {buttonLoadingDelete==='CATEGORY' ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-talaera-blue" />
                    ) : (
                      <img src="/delete.svg" alt="Delete" className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Thin progress bar between text and buttons with color coding */}
              {(() => {
                const name = String((selectedDetails as any)?.name || '');
                const first = name.trim().charAt(0).toUpperCase();
                const alphaPos = first >= 'A' && first <= 'Z' ? (first.charCodeAt(0) - 64) : 1;
                const score = ((alphaPos % 10) || 10); // 1-10
                const pct = Math.max(0, Math.min(100, (score / 10) * 100));
                const colorClass = score <= 3 ? 'bg-red-500' : (score <= 7 ? 'bg-yellow-400' : 'bg-green-500');
                return (
                  <div className="mt-2 mb-4">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-2 ${colorClass}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="mt-1 text-xs text-gray-500">My Progress: {score}/10</div>
                  </div>
                );
              })()}

              <div className="flex flex-col md:flex-row justify-between">
                <div className="md:w-1/2 pr-4">
                  <p className="text-xl text-gray-700 mb-4">{selectedDetails?.description || ' '}</p>
                  <div className="flex space-x-4">
                    <button 
                      onClick={() => {
                        const context = {
                          categoryDescription: (selectedDetails as any)?.description,
                          skillExamples: categorySkills.map(s => s.name)
                        };
                        
                        navigateToTally({
                          topic: `Practice ${selected.name} skills with role-play scenarios`,
                          category: selected.name,
                          context: JSON.stringify(context),
                          autoStart: true
                        });
                      }}
                      className="border-2 border-gray-700 text-gray-700 px-6 py-3 rounded-md font-medium hover:bg-gray-100 transition-colors"
                    >
                      Talk to Tally
                    </button>
                    <button className="bg-[#25394D] text-white px-10 py-3 rounded-md font-medium hover:brightness-110 transition-all">Practice Now</button>
                  </div>
                </div>
                <div className="md:w-1/2 mt-6 md:mt-0 mb-8">
                  {imgUrl ? (
                    <img src={imgUrl} alt={selectedDetails?.name || 'category'} className="rounded-lg w-full h-auto" />
                  ) : (
                    <div className="w-full h-64 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">No image</div>
                  )}
                </div>
              </div>
            </div>

            {/* Main content */}
            <div className="border-t border-blue-200 p-0 pb-6 bg-[#F5F1EF]">
              {/* Motivation / Challenge / Transformation */}
              <div className="border border-gray-200 rounded-lg p-6 mb-6 bg-white mx-6 mt-6">
                <h2 className="text-2xl font-semibold text-gray-700 mb-6">{(selectedDetails as any)?.motivation_statement || ' '}</h2>
                <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                  <div className="md:w-1/2 p-6 bg-white rounded-lg border-l-4 border-red-400">
                    <div className="flex items-center mb-3">
                      <div className="text-red-500 mr-2">
                        <i className="fas fa-bullseye text-2xl"></i>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-700">The Challenge</h3>
                    </div>
                    <p className="text-gray-700">{(selectedDetails as any)?.challenge_statement || ' '}</p>
                  </div>
                  <div className="md:w-1/2 p-6 bg-white rounded-lg border-l-4 border-green-500">
                    <div className="flex items-center mb-3">
                      <div className="text-green-500 mr-2">
                        <i className="fas fa-rocket text-2xl"></i>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-700">The Transformation</h3>
                    </div>
                    <p className="text-gray-700">{(selectedDetails as any)?.transformation_statement || ' '}</p>
                  </div>
                </div>
              </div>

              {/* Skills list */}
              <div className="relative border border-gray-200 rounded-lg p-6 bg-white mx-6 mb-6">
                <h2 className="text-2xl font-semibold text-gray-700 text-center mb-8">Skills included</h2>
                <button
                  title="Add Skill"
                  onClick={onAddSkill}
                  className="absolute top-4 right-4 p-2 bg-white/90 hover:bg-white rounded-full shadow border"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5 text-green-600"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v14m7-7H5"/></svg>
                </button>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
                  {categorySkills.length > 0 ? (
                    [...categorySkills].sort((a:any,b:any)=> (a.display_order ?? 0) - (b.display_order ?? 0)).map(s => (
                      <button key={s.id} onClick={()=> openSkill(s)} className="text-gray-700 font-medium hover:text-blue-700 cursor-pointer">
                        {s.name}
                      </button>
                    ))
                  ) : (
                    <div className="col-span-5 text-gray-400">No skills yet</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selected && selected.type === 'CATEGORY' && selectedSkill && !selectedSubskill && (
        <div className="col-span-3">
          <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-sm border">
            {/* Header section (mirrors category header, without icon) */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <button className="text-gray-600 mr-4" onClick={()=> setSelectedSkill(null)} title="Back">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                  </button>
                  <h1 className="text-3xl font-semibold text-gray-700">{selectedSkill.name}</h1>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    title="Edit"
                    onClick={async ()=>{
                      try {
                        setLoading(true);
                        setButtonLoadingEdit('SKILL');
                        const det = await skillsService.getEntityById('SKILL', (selectedSkillDetails as any)?.id || selectedSkill.id);
                        setEditEntityType('SKILL');
                        setEditDetails(det);
                        setEditImgUrl((det as any)?.image ? await skillsService.presignView((det as any).image) : null);
                        setEditIconUrl(null);
                        setEditModalOpen(true);
                      } catch (err:any) { setError(err?.message || 'Failed to open editor'); } finally { setLoading(false); setButtonLoadingEdit(null); }
                    }}
                    className="p-2 bg-white/90 hover:bg-white rounded-full shadow border"
                  >
                    {buttonLoadingEdit==='SKILL' ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-talaera-blue" />
                    ) : (
                      <img src="/pencil-edit.svg" alt="Edit" className="h-5 w-5" />
                    )}
                  </button>
                  <button
                    title="Move"
                    onClick={async ()=>{
                      if (selectedSkillDetails) {
                        await handleMoveClick('SKILL', selectedSkillDetails);
                      }
                    }}
                    className="p-2 bg-white/90 hover:bg-white rounded-full shadow border"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                  </button>
                  <button
                    title="Delete"
                    onClick={async ()=>{
                      try {
                        setButtonLoadingDelete('SKILL');
                        await requestDelete('SKILL', (selectedSkillDetails as any)?.id || selectedSkill.id, selectedSkill.name, (selectedDetails as any)?.id);
                      } finally { setButtonLoadingDelete(null); }
                    }}
                    className="p-2 bg-white/90 hover:bg-white rounded-full shadow border"
                  >
                    {buttonLoadingDelete==='SKILL' ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-talaera-blue" />
                    ) : (
                      <img src="/delete.svg" alt="Delete" className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex flex-col md:flex-row justify-between">
                <div className="md:w-1/2 pr-4">
                  <p className="text-xl text-gray-700 mb-8">{selectedSkillDetails?.description || ' '}</p>
                  <div className="flex space-x-4">
                    <button 
                      onClick={() => {
                        const context = {
                          categoryDescription: (selectedDetails as any)?.description,
                          skillDescription: (selectedSkillDetails as any)?.description,
                          subskills: selectedSkillSubskills.map(s => s.name)
                        };
                        
                        navigateToTally({
                          topic: `Practice ${selectedSkill.name} skill`,
                          category: (selectedDetails as any)?.name,
                          skill: selectedSkill.name,
                          context: JSON.stringify(context),
                          autoStart: true
                        });
                      }}
                      className="border-2 border-gray-700 text-gray-700 px-6 py-3 rounded-md font-medium hover:bg-gray-100 transition-colors"
                    >
                      Talk to Tally
                    </button>
                    <button className="bg-[#25394D] text-white px-10 py-3 rounded-md font-medium hover:brightness-110 transition-all">Practice Now</button>
                  </div>
                </div>
                <div className="md:w-1/2 mt-6 md:mt-0">
                  {selectedSkillImgUrl ? (
                    <img src={selectedSkillImgUrl} alt={selectedSkill.name} className="rounded-lg w-full h-auto" />
                  ) : (
                    <div className="w-full h-64 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">No Image Available</div>
                  )}
                </div>
              </div>
            </div>

            {/* Subskills list as text links (like category skills) */}
            <div className="border-t border-blue-200 p-0 pb-6 bg-[#F5F1EF]">
              <div className="relative border border-gray-200 rounded-lg p-6 bg-white mx-6 my-6">
                <h2 className="text-2xl font-semibold text-gray-700 text-center mb-8">Subskills</h2>
                <button
                  title="Add Subskill"
                  onClick={onAddSubskill}
                  className="absolute top-4 right-4 p-2 bg-white/90 hover:bg-white rounded-full shadow border"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5 text-green-600"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v14m7-7H5"/></svg>
                </button>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
                  {selectedSkillSubskills.length > 0 ? (
                    selectedSkillSubskills.map(ss => (
                      <button key={ss.id} onClick={()=> openSubskill({ id: ss.id, name: ss.name })} className="text-gray-700 font-medium hover:text-blue-700 cursor-pointer">
                        {ss.name}
                      </button>
                    ))
                  ) : (
                    <div className="col-span-5 text-gray-400">No subskills</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Edit Modal (Category/Skill/Subskill) */}
    {editModalOpen && (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/30" onClick={() => setEditModalOpen(false)} />
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-lg shadow-xl border">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Edit {editEntityType}</h3>
              <button onClick={() => setEditModalOpen(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-5 max-h-[70vh] overflow-auto">
              {editDetails ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name</label>
                      <input className="w-full border rounded p-2" value={editDetails.name || ''} onChange={(e)=> setEditDetails({ ...editDetails, name: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Beginner Name</label>
                      <input className="w-full border rounded p-2" value={editDetails.beginner_name || ''} onChange={(e)=> setEditDetails({ ...editDetails, beginner_name: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Display Order</label>
                      <input type="number" className="w-full border rounded p-2" value={editDetails.display_order ?? 0} onChange={(e)=> setEditDetails({ ...editDetails, display_order: Number(e.target.value) })} />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <textarea className="w-full border rounded p-2" rows={2} value={editDetails.description || ''} onChange={(e)=> setEditDetails({ ...editDetails, description: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Beginner Description</label>
                      <textarea className="w-full border rounded p-2" rows={2} value={editDetails.beginner_description || ''} onChange={(e)=> setEditDetails({ ...editDetails, beginner_description: e.target.value })} />
                    </div>
                  </div>
                  {editEntityType === 'CATEGORY' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Motivation</label>
                        <textarea className="w-full border rounded p-2" rows={2} value={editDetails.motivation_statement || ''} onChange={(e)=> setEditDetails({ ...editDetails, motivation_statement: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Challenge</label>
                        <textarea className="w-full border rounded p-2" rows={2} value={editDetails.challenge_statement || ''} onChange={(e)=> setEditDetails({ ...editDetails, challenge_statement: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Transformation</label>
                        <textarea className="w-full border rounded p-2" rows={2} value={editDetails.transformation_statement || ''} onChange={(e)=> setEditDetails({ ...editDetails, transformation_statement: e.target.value })} />
                      </div>
                    </>
                  )}
                  {editEntityType === 'SUBSKILL' && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Explainer</label>
                          <textarea className="w-full border rounded p-2" rows={2} value={editDetails.explainer || ''} onChange={(e)=> setEditDetails({ ...editDetails, explainer: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">QC Status</label>
                          <input className="w-full border rounded p-2" value={editDetails.qc_status || ''} onChange={(e)=> setEditDetails({ ...editDetails, qc_status: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Explainer (Student Normal)</label>
                          <textarea className="w-full border rounded p-2" rows={2} value={editDetails.explainer_student_normal || ''} onChange={(e)=> setEditDetails({ ...editDetails, explainer_student_normal: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Explainer (Student Beginner)</label>
                          <textarea className="w-full border rounded p-2" rows={2} value={editDetails.explainer_student_beginner || ''} onChange={(e)=> setEditDetails({ ...editDetails, explainer_student_beginner: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Category Tags (comma-separated)</label>
                          <input className="w-full border rounded p-2" value={(editDetails.category_tags || []).join(', ')} onChange={(e)=> setEditDetails({ ...editDetails, category_tags: e.target.value.split(',').map((s)=> s.trim()).filter(Boolean) })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">CEFR Levels (comma-separated)</label>
                          <input className="w-full border rounded p-2" value={(editDetails.cefr_levels || []).join(', ')} onChange={(e)=> setEditDetails({ ...editDetails, cefr_levels: e.target.value.split(',').map((s)=> s.trim()).filter(Boolean) })} />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Examples: Beginner (one per line)</label>
                          <textarea className="w-full border rounded p-2" rows={4} value={((editDetails.examples?.beginner) || []).join('\n')} onChange={(e)=> setEditDetails({ ...editDetails, examples: { ...(editDetails.examples || {}), beginner: e.target.value.split('\n').map(s=> s.trim()).filter(Boolean) } })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Examples: Intermediate</label>
                          <textarea className="w-full border rounded p-2" rows={4} value={((editDetails.examples?.intermediate) || []).join('\n')} onChange={(e)=> setEditDetails({ ...editDetails, examples: { ...(editDetails.examples || {}), intermediate: e.target.value.split('\n').map(s=> s.trim()).filter(Boolean) } })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Examples: Advanced</label>
                          <textarea className="w-full border rounded p-2" rows={4} value={((editDetails.examples?.advanced) || []).join('\n')} onChange={(e)=> setEditDetails({ ...editDetails, examples: { ...(editDetails.examples || {}), advanced: e.target.value.split('\n').map(s=> s.trim()).filter(Boolean) } })} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Counter Examples (one per line)</label>
                          <textarea className="w-full border rounded p-2" rows={3} value={((editDetails.counter_examples) || []).join('\n')} onChange={(e)=> setEditDetails({ ...editDetails, counter_examples: e.target.value.split('\n').map(s=> s.trim()).filter(Boolean) })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Practice Prompts</label>
                          <textarea className="w-full border rounded p-2" rows={3} value={((editDetails.practice_prompts) || []).join('\n')} onChange={(e)=> setEditDetails({ ...editDetails, practice_prompts: e.target.value.split('\n').map(s=> s.trim()).filter(Boolean) })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Assessment Criteria</label>
                          <textarea className="w-full border rounded p-2" rows={3} value={((editDetails.assessment_criteria) || []).join('\n')} onChange={(e)=> setEditDetails({ ...editDetails, assessment_criteria: e.target.value.split('\n').map(s=> s.trim()).filter(Boolean) })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Collocations</label>
                          <textarea className="w-full border rounded p-2" rows={3} value={((editDetails.collocations) || []).join('\n')} onChange={(e)=> setEditDetails({ ...editDetails, collocations: e.target.value.split('\n').map(s=> s.trim()).filter(Boolean) })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Usage Notes</label>
                          <textarea className="w-full border rounded p-2" rows={3} value={((editDetails.usage_notes) || []).join('\n')} onChange={(e)=> setEditDetails({ ...editDetails, usage_notes: e.target.value.split('\n').map(s=> s.trim()).filter(Boolean) })} />
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700">Structured Examples (JSON array)</label>
                        <textarea className="w-full border rounded p-2 font-mono text-xs" rows={4} value={JSON.stringify(editDetails.examples_structured || [], null, 2)} onChange={(e)=>{
                          try {
                            const val = JSON.parse(e.target.value || '[]');
                            setEditDetails({ ...editDetails, examples_structured: Array.isArray(val) ? val : [] });
                          } catch { /* ignore parse errors, user can fix */ }
                        }} />
                      </div>
                    </>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium mb-1">Image</div>
                      {editImgUrl && <img src={editImgUrl ?? undefined} alt="category" className="w-40 h-40 object-cover rounded mb-2" />}
                      <input type="file" accept="image/*" onChange={async (e)=>{
                        const file = e.target.files?.[0]; if (!file || !editDetails) return;
                        try {
                          setSaving(true);
                          const key = editEntityType === 'CATEGORY'
                            ? `categories/${editDetails.id}/image.png`
                            : editEntityType === 'SKILL'
                              ? `skills/${editDetails.id}/image.png`
                              : `subskills/${editDetails.id}/image.png`;
                          const presign = await skillsService.presignUpload(key, 'image/png');
                          await fetch(presign.url, { method: 'PUT', headers: { 'Content-Type': 'image/png' }, body: file });
                          setEditImgUrl(await skillsService.presignView(key));
                          setEditDetails({ ...editDetails, image: key });
                        } catch (err:any) { setError(err?.message || 'Upload failed'); } finally { setSaving(false); }
                      }} />
                    </div>
                    {editEntityType === 'CATEGORY' && (
                      <div>
                        <div className="text-sm font-medium mb-1">Icon</div>
                        {editIconUrl && <img src={editIconUrl ?? undefined} alt="icon" className="w-10 h-10 object-cover rounded mb-2" />}
                        <input type="file" accept="image/*" onChange={async (e)=>{
                          const file = e.target.files?.[0]; if (!file || !editDetails) return;
                          try {
                            setSaving(true);
                            const key = `categories/${editDetails.id}/icon.png`;
                            const presign = await skillsService.presignUpload(key, 'image/png');
                            await fetch(presign.url, { method: 'PUT', headers: { 'Content-Type': 'image/png' }, body: file });
                            setEditIconUrl(await skillsService.presignView(key));
                            setEditDetails({ ...editDetails, icon: key });
                          } catch (err:any) { setError(err?.message || 'Upload failed'); } finally { setSaving(false); }
                        }} />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-gray-500">Loading...</div>
              )}
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300" onClick={()=> setEditModalOpen(false)}>Cancel</button>
              <button disabled={saving} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700" onClick={async () =>{
                if (!editDetails) return;
                try {
                  setSaving(true);
                  // Determine parentId requirements
                  let resolvedParentId: string | undefined = undefined;
                  if (editEntityType === 'SKILL') {
                    resolvedParentId = (selectedSkillDetails as any)?.parentId || (selectedSkillDetails as any)?.parent_id || (selectedDetails as any)?.id;
                    if (!resolvedParentId) {
                      setSaving(false);
                      setError('Parent category id not found; cannot save skill.');
                      return;
                    }
                    // Uniqueness: check against existing skills in category
                    const dup = (categorySkills || []).some(s => s.name.trim().toLowerCase() === String(editDetails.name||'').trim().toLowerCase());
                    if (dup && !editDetails.id) {
                      setSaving(false);
                      setError('A skill with this name already exists in this category.');
                      return;
                    }
                  } else if (editEntityType === 'SUBSKILL') {
                    resolvedParentId = (selectedSubskillDetails as any)?.parentId || (selectedSubskillDetails as any)?.parent_id || (selectedSkill as any)?.id;
                    if (!resolvedParentId) {
                      setSaving(false);
                      setError('Parent skill id not found; cannot save subskill.');
                      return;
                    }
                    // Uniqueness: check against existing subskills in skill
                    const dup = (selectedSkillSubskills || []).some(ss => ss.name.trim().toLowerCase() === String(editDetails.name||'').trim().toLowerCase());
                    if (dup && !editDetails.id) {
                      setSaving(false);
                      setError('A subskill with this name already exists in this skill.');
                      return;
                    }
                  }

                  const payload:any = {
                    entityType: editEntityType,
                    id: editDetails.id,
                    name: editDetails.name,
                    beginner_name: editDetails.beginner_name,
                    description: editDetails.description,
                    beginner_description: editDetails.beginner_description,
                    display_order: Number(editDetails.display_order ?? 0),
                    image: editDetails.image,
                    icon: editEntityType === 'CATEGORY' ? editDetails.icon : undefined,
                    motivation_statement: editEntityType === 'CATEGORY' ? editDetails.motivation_statement : undefined,
                    challenge_statement: editEntityType === 'CATEGORY' ? editDetails.challenge_statement : undefined,
                    transformation_statement: editEntityType === 'CATEGORY' ? editDetails.transformation_statement : undefined,
                    // Subskill-specific fields
                    explainer: editEntityType === 'SUBSKILL' ? (editDetails.explainer ?? (selectedSubskillDetails as any)?.explainer) : undefined,
                    explainer_student_normal: editEntityType === 'SUBSKILL' ? (editDetails.explainer_student_normal ?? (selectedSubskillDetails as any)?.explainer_student_normal) : undefined,
                    explainer_student_beginner: editEntityType === 'SUBSKILL' ? (editDetails.explainer_student_beginner ?? (selectedSubskillDetails as any)?.explainer_student_beginner) : undefined,
                    qc_status: editEntityType === 'SUBSKILL' ? (editDetails.qc_status ?? (selectedSubskillDetails as any)?.qc_status) : undefined,
                    category_tags: editEntityType === 'SUBSKILL' ? ((editDetails.category_tags ?? (selectedSubskillDetails as any)?.category_tags) || []) : undefined,
                    cefr_levels: editEntityType === 'SUBSKILL' ? ((editDetails.cefr_levels ?? (selectedSubskillDetails as any)?.cefr_levels) || []) : undefined,
                    examples: editEntityType === 'SUBSKILL' ? (editDetails.examples ?? (selectedSubskillDetails as any)?.examples ?? { beginner: [], intermediate: [], advanced: [] }) : undefined,
                    examples_structured: editEntityType === 'SUBSKILL' ? (editDetails.examples_structured ?? (selectedSubskillDetails as any)?.examples_structured ?? []) : undefined,
                    counter_examples: editEntityType === 'SUBSKILL' ? ((editDetails.counter_examples ?? (selectedSubskillDetails as any)?.counter_examples) || []) : undefined,
                    practice_prompts: editEntityType === 'SUBSKILL' ? ((editDetails.practice_prompts ?? (selectedSubskillDetails as any)?.practice_prompts) || []) : undefined,
                    assessment_criteria: editEntityType === 'SUBSKILL' ? ((editDetails.assessment_criteria ?? (selectedSubskillDetails as any)?.assessment_criteria) || []) : undefined,
                    collocations: editEntityType === 'SUBSKILL' ? ((editDetails.collocations ?? (selectedSubskillDetails as any)?.collocations) || []) : undefined,
                    usage_notes: editEntityType === 'SUBSKILL' ? ((editDetails.usage_notes ?? (selectedSubskillDetails as any)?.usage_notes) || []) : undefined,
                    parentId: resolvedParentId,
                  };
                  const saved = await skillsService.saveEntity(payload);
                  console.log('UI: save response payload', saved);
                  // refresh cards / lists with updated display_order
                  setCategoryCards((prev)=> prev.map((c)=> c.id===editDetails.id ? {
                    ...c,
                    name: payload.name,
                    desc: payload.description,
                    display_order: payload.display_order,
                    imageUrl: editImgUrl || c.imageUrl,
                    iconUrl: editEntityType === 'CATEGORY' ? (editIconUrl || c.iconUrl) : c.iconUrl,
                  } : c));
                  // refresh currently viewed panels
                  if (editEntityType === 'CATEGORY') {
                    setSelectedDetails(saved as any);
                    setImgUrl(editImgUrl || (imgUrl ?? null));
                    setIconUrl(editIconUrl || (iconUrl ?? null));
                  } else if (editEntityType === 'SKILL') {
                    setSelectedSkillDetails(saved as any);
                    setSelectedSkill({ id: (saved as any).id, name: (saved as any).name });
                    setSelectedSkillImgUrl(editImgUrl || (selectedSkillImgUrl ?? null));
                    // update listing name if changed
                    setCategorySkills((prev)=> prev.map(s=> s.id === (saved as any).id ? { id: s.id, name: (saved as any).name, display_order: (saved as any).display_order } : s));
                  } else if (editEntityType === 'SUBSKILL') {
                    // Re-fetch to ensure canonical data saved server-side
                    const refreshed = await skillsService.getEntityById('SUBSKILL', (saved as any).id || editDetails.id);
                    setSelectedSubskillDetails(refreshed as any);
                    setSelectedSubskill({ id: (refreshed as any).id, name: (refreshed as any).name });
                    setSelectedSubskillImgUrl(editImgUrl || (selectedSubskillImgUrl ?? null));
                    // update listing name if changed
                    setSelectedSkillSubskills((prev)=> prev.map(ss=> ss.id === (refreshed as any).id ? { ...ss, name: (refreshed as any).name, imageUrl: editImgUrl || ss.imageUrl, display_order: (refreshed as any).display_order } : ss));
                  }
                  setEditModalOpen(false);
                } catch (err:any) { setError(err?.message || 'Save failed'); } finally { setSaving(false); }
              }}>Save</button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Delete Confirmation Modal */}
    {deleteModalOpen && deleteInfo && (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/30" onClick={() => setDeleteModalOpen(false)} />
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-lg shadow-xl border">
            <div className="px-5 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Delete {(deleteInfo as any).entityType}</h3>
            </div>
            <div className="p-5">
              {(deleteInfo as any).hasChildren ? (
                <div className="text-gray-700">
                  "{(deleteInfo as any).name}" cannot be deleted because it has {(deleteInfo as any).childrenCount} child node{(deleteInfo as any).childrenCount===1?'':'s'}. Please delete the child node{(deleteInfo as any).childrenCount===1?'':'s'} first.
                </div>
              ) : (
                <div className="text-gray-700">
                  Are you sure you want to delete "{(deleteInfo as any).name}"? This action cannot be undone.
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300" onClick={()=> setDeleteModalOpen(false)}>Cancel</button>
              {!((deleteInfo as any).hasChildren) && (
                <button className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700" onClick={confirmDelete}>Delete</button>
              )}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Move Modal */}
    {moveModalOpen && moveEntityDetails && (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/30" onClick={() => setMoveModalOpen(false)} />
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-lg shadow-xl border">
            <div className="px-5 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Move {moveEntityType}</h3>
            </div>
            <div className="p-5">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Parent</label>
                <div className="p-2 bg-gray-100 rounded text-gray-600">
                  {availableParents.find(p => p.id === moveEntityDetails.parent_id)?.name || 'Unknown'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Parent</label>
                <select 
                  className="w-full border rounded p-2"
                  value={selectedNewParent}
                  onChange={(e) => setSelectedNewParent(e.target.value)}
                >
                  <option value="">Select a parent</option>
                  {availableParents
                    .filter(p => p.id !== moveEntityDetails.parent_id)
                    .map(parent => (
                      <option key={parent.id} value={parent.id}>{parent.name}</option>
                    ))
                  }
                </select>
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button 
                className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300" 
                onClick={() => setMoveModalOpen(false)}
                disabled={movingEntity}
              >
                Cancel
              </button>
              <button 
                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50" 
                onClick={handleMove}
                disabled={!selectedNewParent || movingEntity}
              >
                {movingEntity ? 'Moving...' : 'Move'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Example Delete Confirmation */}
    {exampleDeleteOpen && exampleDeleteIndex != null && (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/30" onClick={() => setExampleDeleteOpen(false)} />
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-lg shadow-xl border">
            <div className="px-5 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Delete Example</h3>
            </div>
            <div className="p-5">
              <div className="text-gray-700">Are you sure you want to delete this example?</div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300" onClick={()=> setExampleDeleteOpen(false)}>Cancel</button>
              <button className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700" onClick={async ()=>{
                if (exampleDeleteIndex == null || !selectedSubskillDetails) { setExampleDeleteOpen(false); return; }
                try {
                  const current = (selectedSubskillDetails as any);
                  const all = Array.isArray(current.examples_structured) ? [...current.examples_structured] : [];
                  all.splice(exampleDeleteIndex, 1);
                  const payload:any = {
                    entityType: 'SUBSKILL',
                    id: current.id,
                    name: current.name,
                    description: current.description,
                    beginner_name: current.beginner_name,
                    beginner_description: current.beginner_description,
                    display_order: Number(current.display_order ?? 0),
                    image: current.image,
                    explainer: current.explainer,
                    explainer_student_normal: current.explainer_student_normal,
                    explainer_student_beginner: current.explainer_student_beginner,
                    qc_status: current.qc_status,
                    category_tags: current.category_tags || [],
                    cefr_levels: current.cefr_levels || [],
                    examples: current.examples || { beginner: [], intermediate: [], advanced: [] },
                    examples_structured: all,
                    counter_examples: current.counter_examples || [],
                    practice_prompts: current.practice_prompts || [],
                    assessment_criteria: current.assessment_criteria || [],
                    collocations: current.collocations || [],
                    usage_notes: current.usage_notes || [],
                    parentId: (selectedSkill as any)?.id || current.parent_id,
                  };
                  const saved = await skillsService.saveEntity(payload);
                  setSelectedSubskillDetails(saved as any);
                } finally {
                  setExampleDeleteOpen(false);
                  setExampleDeleteIndex(null);
                }
              }}>Delete</button>
            </div>
          </div>
        </div>
      </div>
    )}
    {/* Example Edit Modal */}
    {exampleEditOpen && editingExample && (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/30" onClick={() => setExampleEditOpen(false)} />
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-lg shadow-xl border">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Edit Example</h3>
              <button onClick={() => setExampleEditOpen(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-5">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Level</label>
                  <select className="w-full border rounded p-2" value={editingExample.level} onChange={(e)=> setEditingExample({ ...editingExample, level: e.target.value })}>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Context</label>
                  <textarea className="w-full border rounded p-2" rows={3} value={editingExample.context || ''} onChange={(e)=> setEditingExample({ ...editingExample, context: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Text</label>
                  <textarea className="w-full border rounded p-2" rows={3} value={editingExample.text || ''} onChange={(e)=> setEditingExample({ ...editingExample, text: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300" onClick={()=> setExampleEditOpen(false)}>Cancel</button>
              <button disabled={savingExample} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700" onClick={async ()=>{
                if (!selectedSubskillDetails) { setExampleEditOpen(false); return; }
                try {
                  setSavingExample(true);
                  const current = (selectedSubskillDetails as any);
                  const all = Array.isArray(current.examples_structured) ? [...current.examples_structured] : [];
                  if (editingExampleIndex == null) {
                    // Creating new example
                    const newEx = { example_id: editingExample.example_id || generateShortId(), level: String(editingExample.level || '').toLowerCase(), text: editingExample.text, context: editingExample.context };
                    all.push(newEx);
                  } else {
                    // Updating existing
                    const updated = { ...all[editingExampleIndex], level: String(editingExample.level || '').toLowerCase(), text: editingExample.text, context: editingExample.context };
                    all[editingExampleIndex] = updated;
                  }
                  const payload:any = {
                    entityType: 'SUBSKILL',
                    id: current.id,
                    name: current.name,
                    description: current.description,
                    beginner_name: current.beginner_name,
                    beginner_description: current.beginner_description,
                    display_order: Number(current.display_order ?? 0),
                    image: current.image,
                    explainer: current.explainer,
                    explainer_student_normal: current.explainer_student_normal,
                    explainer_student_beginner: current.explainer_student_beginner,
                    qc_status: current.qc_status,
                    category_tags: current.category_tags || [],
                    cefr_levels: current.cefr_levels || [],
                    examples: current.examples || { beginner: [], intermediate: [], advanced: [] },
                    examples_structured: all,
                    counter_examples: current.counter_examples || [],
                    practice_prompts: current.practice_prompts || [],
                    assessment_criteria: current.assessment_criteria || [],
                    collocations: current.collocations || [],
                    usage_notes: current.usage_notes || [],
                    parentId: (selectedSkill as any)?.id || current.parent_id,
                  };
                  const saved = await skillsService.saveEntity(payload);
                  const refreshed = await skillsService.getEntityById('SUBSKILL', (saved as any).id || current.id);
                  setSelectedSubskillDetails(refreshed as any);
                  // reflect in list if name changed (unlikely here)
                  setExampleEditOpen(false);
                } catch (err:any) { setError(err?.message || 'Failed to save example'); }
                finally { setSavingExample(false); }
              }}>Save</button>
            </div>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default SkillsExplorerV2;


